import React from 'react';
import { Phone, MessageSquare, Mail, Send, Loader2 } from 'lucide-react';
import { siteConfig } from '../data';
import { motion } from 'framer-motion';

export default function ContactSection() {
  const [status, setStatus] = React.useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    const formData = new FormData(e.target);
    const data = {
      ...Object.fromEntries(formData.entries()),
      to: 'support@onlinetryghed.dk',
      _to: 'support@onlinetryghed.dk'
    };

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <section id="kontakt" className="py-16 bg-[#103B66]">
      <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="px-4 sm:px-0"
          >
            <h2 className="text-2xl md:text-5xl font-bold text-white mb-4 md:mb-8">
              Få hjælp i dag
            </h2>
            <p className="text-lg md:text-xl text-white mb-8 md:mb-12 leading-relaxed">
              Vi sidder klar til at hjælpe dig alle dage mellem 9 og 19. 
              Du kan ringe, sende en SMS eller udfylde formularen.
            </p>

            <div className="space-y-4 sm:space-y-6">
              <a
                href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
                className="flex items-center p-4 sm:p-6 bg-white shadow-sm border border-slate-100 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#103B66] text-white flex items-center justify-center mr-4 sm:mr-6 group-hover:scale-110 transition-transform shrink-0">
                  <Phone className="w-5 h-5 sm:w-8 sm:h-8" />
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-slate-500 font-medium">Ring til os</div>
                  <div className="text-lg sm:text-2xl font-bold text-[#103B66]">{siteConfig.phone}</div>
                </div>
              </a>

              <a
                href={`sms:${(siteConfig.sms || '93402717').replace(/\s/g, '')}`}
                className="flex items-center p-4 sm:p-6 bg-white shadow-sm border border-slate-100 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#103B66] text-white flex items-center justify-center mr-4 sm:mr-6 group-hover:scale-110 transition-transform shrink-0">
                  <MessageSquare className="w-5 h-5 sm:w-8 sm:h-8" />
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-slate-500 font-medium">Send en SMS</div>
                  <div className="text-lg sm:text-2xl font-bold text-[#103B66]">{siteConfig.sms || '93 40 27 17'}</div>
                </div>
              </a>

              <a
                href={`mailto:${siteConfig.email}`}
                className="flex items-center p-4 sm:p-6 bg-white shadow-sm border border-slate-100 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#103B66] text-white flex items-center justify-center mr-4 sm:mr-6 group-hover:scale-110 transition-transform shrink-0">
                  <Mail className="w-5 h-5 sm:w-8 sm:h-8" />
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-slate-500 font-medium">Send en mail</div>
                  <div className="text-base sm:text-xl font-bold text-[#103B66] break-all">{siteConfig.email}</div>
                </div>
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white p-5 md:p-12 shadow-xl border border-slate-100"
          >
            <h3 className="text-xl md:text-2xl font-bold text-[#103B66] mb-6 md:mb-8">Book en tryghedssamtale</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Recipient configuration */}
              <input type="hidden" name="to" value="support@onlinetryghed.dk" />
              <input type="hidden" name="_to" value="support@onlinetryghed.dk" />

              {/* Honeypot */}
              <div className="sr-only absolute opacity-0 -z-10">
                <label htmlFor="website_url">Website URL</label>
                <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Navn</label>
                <input
                  required
                  type="text"
                  name="name"
                  className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-none focus:outline-none focus:ring-2 focus:ring-[#103B66] transition-all"
                  placeholder="Dit fulde navn"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Telefon</label>
                <input
                  required
                  type="tel"
                  name="phone"
                  className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-none focus:outline-none focus:ring-2 focus:ring-[#103B66] transition-all"
                  placeholder="Dit telefonnummer"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Hvad kan vi hjælpe med?</label>
                <textarea
                  required
                  name="message"
                  rows="4"
                  className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  placeholder="Skriv kort, hvad du har brug for hjælp til..."
                ></textarea>
              </div>

              <button
                disabled={status === 'loading'}
                type="submit"
                className="w-full py-3 bg-[#103B66] text-white font-bold hover:bg-slate-800 hover:-translate-y-1 transition-all flex items-center justify-center disabled:opacity-50"
              >
                {status === 'loading' ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <>
                    Send besked
                    <Send className="ml-2 w-5 h-5" />
                  </>
                )}
              </button>

              {status === 'success' && (
                <div className="p-4 bg-green-50 text-green-700 text-center font-medium">
                  Tak for din henvendelse. Du bliver hurtigst muligt kontaktet af en tryghedskonsulent. Hav en tryg dag 😊
                </div>
              )}
              {status === 'error' && (
                <div className="p-4 bg-red-50 text-red-700 text-center font-medium">
                  Der skete en fejl. Prøv venligst igen eller ring til os.
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
